import { Box, Typography } from '@mui/material';
import React from 'react';
import { KOSTEN_ARTEN, KOSTEN_POSITION, NACHWEIS_POSITION_STATUS, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';

import { get } from 'lodash';
import SummaryLine from 'src/components/ui-component/form-component/abschluss-page/SummaryLine';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import Schema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
import NachweisPositionSchema from 'src/lib/paths/nachweisposition_FULL_Paths.json';
import { InhaltTrenner } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import { DisplayEuroMUI } from 'src/utils/formats';
const MA_P = Schema.attributes;
const NP_P = NachweisPositionSchema.attributes;

export const MittelabrufStipendium = () => {
	const { application } = useApplication();

	// const foerderquote = get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Foerderquote_Gesamt.path, 0);
	const foerderung = Number(get(application, MA_P.Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung.path, 0)) || 0;
	const bereitsAusgezahlt = Number(get(application, MA_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path, 0)) || 0;
	const verfuegbareFoerderungDiesesHaushaltsjahr = Math.max(0, foerderung - bereitsAusgezahlt);
	const nachweispositionen = get(application, MA_P.Mittelabruf_Nachweispositions.path, []);
	const getAusgabenByStatus = (status: string) => {
		const nachweisposition = (nachweispositionen as any[]).find((eachPosition: any) => get(eachPosition, NP_P.Allgemein_Status.path) === status);
		return Number(get(nachweisposition, NP_P.Allgemein_Ausgaben_Kosten.path, 0)) || 0;
	};
	const getEinnahmePositionByKostenart = (kostenart: string) =>
		(nachweispositionen as any[]).find(
			(eachPosition: any) =>
				get(eachPosition, NP_P.Allgemein_Kostenart.path) === KOSTEN_POSITION.Einnahme &&
				get(eachPosition, NP_P.Allgemein_Kostenart_Finanzierung.path) === kostenart,
		);
	const eigenmittelPosition = getEinnahmePositionByKostenart(KOSTEN_ARTEN.Eigenmittel);
	const oeffentlicheMittelPosition = getEinnahmePositionByKostenart(KOSTEN_ARTEN.OeffentlicheMittel);
	const projektausgabenAngefallen = getAusgabenByStatus(NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben);
	const projektausgabenGeplant = getAusgabenByStatus(NACHWEIS_POSITION_STATUS.geplant);
	const projektausgabenGesamt = projektausgabenAngefallen + projektausgabenGeplant;
	const eigenmittelBetrag = Number(get(eigenmittelPosition, NP_P.Allgemein_Ausgaben_Kosten.path, 0)) || 0;
	const oeffentlicheMittelBetrag = Number(get(oeffentlicheMittelPosition, NP_P.Allgemein_Ausgaben_Kosten.path, 0)) || 0;
	const eigenUndDrittmittelGesamt = eigenmittelBetrag + oeffentlicheMittelBetrag;
	const eigenmittelLabel =
		get(eigenmittelPosition, NP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Bezeichnung.path, '') ||
		get(eigenmittelPosition, NP_P.Allgemein_Bezeichnung.path, 'Eigenmittel');
	const oeffentlicheMittelLabel =
		get(oeffentlicheMittelPosition, NP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Bezeichnung.path, '') ||
		get(oeffentlicheMittelPosition, NP_P.Allgemein_Bezeichnung.path, 'öffentliche Mittel');
	const bemessungsgrundlage = Number(projektausgabenGesamt - eigenUndDrittmittelGesamt) || 0;
	const berechneteMaxFoerderung = Number(get(application, MA_P.Ermittlung_Des_Auszahlungsbetrages_Anforderungsbetrag.path, 0)) || 0;
	const verbleibendeFoerderung = Math.max(0, verfuegbareFoerderungDiesesHaushaltsjahr - berechneteMaxFoerderung);
	const alert = bemessungsgrundlage - bereitsAusgezahlt > verfuegbareFoerderungDiesesHaushaltsjahr;

	return (
		<>
			<ApplicationHeading title='Mittelabruf' description={PLATZHALTER_TEXT} />
			{alert && (
				<MUIAlert
					infoType='warning'
					message='Der berechnete Mittelabruf übersteigt die Förderung für dieses Projektjahr. Es kann maximal die verfügbare Förderung abgerufen werden.'
				/>
			)}
			<Typography variant='body1' sx={{ mb: 2, mt: 2 }}>
				Die Berechnung des Mittelabrufs basiert auf den von Ihnen gemachten Angaben, wird automatisch generiert und steht unter dem Vorbehalt
				einer fachlichen Prüfung durch die Hessen Agentur.
			</Typography>
			<Box sx={{ mb: 2 }}>
				<SummaryLine label='Projektausgaben' value={DisplayEuroMUI(projektausgabenGesamt)} boldLabel={true} alignValueRight={true} />
				<SummaryLine
					label='bis jetzt angefallene Projektausgaben'
					value={DisplayEuroMUI(projektausgabenAngefallen)}
					alignValueRight={true}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					valueFontWeight={400}
				/>
				<SummaryLine
					label={`in den nächsten drei Monaten anfallende Projektausgaben`}
					value={DisplayEuroMUI(projektausgabenGeplant)}
					alignValueRight={true}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					valueFontWeight={400}
				/>
				<SummaryLine
					label='Eigenmittel und öffentliche Mittel'
					value={`${DisplayEuroMUI(eigenUndDrittmittelGesamt)}`}
					boldLabel={true}
					alignValueRight={true}
				/>
				<SummaryLine
					label={`abzügl. ${eigenmittelLabel}`}
					value={DisplayEuroMUI(eigenmittelBetrag)}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					alignValueRight={true}
					valueFontWeight={400}
				/>
				<SummaryLine
					label={`abzügl. ${oeffentlicheMittelLabel}`}
					value={DisplayEuroMUI(oeffentlicheMittelBetrag)}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					alignValueRight={true}
					valueFontWeight={400}
				/>
				<SummaryLine
					label='Bemessungsgrundlage'
					value={DisplayEuroMUI(bemessungsgrundlage)}
					boldLabel={true}
					alignValueRight={true}
					showBackground={true}
				/>
				<SummaryLine
					label='abzügl. der bereits abgerufenen Zuwendungen im gleichen Haushaltsjahr'
					value={DisplayEuroMUI(bereitsAusgezahlt)}
					alignValueRight={true}
					valueFontWeight={400}
				/>
				<SummaryLine
					label='Mittelabruf'
					value={
						<>
							{DisplayEuroMUI(berechneteMaxFoerderung)}
							{alert && (
								<>
									<br />
									<Typography component='span' variant='caption' sx={{ display: 'inline-block', mt: 0.5, fontWeight: 400 }}>
										Hinweis: Der Abruf wurde auf die maximal abrufbare Zuwendung gekürzt.
									</Typography>
								</>
							)}
						</>
					}
					boldLabel={true}
					showBackground={true}
					// labelPadding={'0.75rem 2rem 0.75rem 0'}
					alignValueRight={true}
				/>
			</Box>
			<Box sx={{ mt: 8, mb: 4 }}>
				<SummaryLine
					label='Gesamtzuwendung in diesem Haushaltsjahr gemäß Bescheid'
					value={DisplayEuroMUI(foerderung)}
					boldLabel={true}
					alignValueRight={true}
					showTopDivider={true}
				/>
				<SummaryLine
					label={
						<>
							Verbleibende Zuwendungssumme <span style={{ textDecoration: 'underline' }}>in diesem Haushaltsjahr</span> (nach diesem
							Mittelabruf)
						</>
					}
					value={DisplayEuroMUI(verbleibendeFoerderung)}
					alignValueRight={true}
					valueFontWeight={400}
				/>
			</Box>
			<InhaltTrenner dotted={true} />
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				Ausgaben können nur bis zur im Förderbescheid festgesetzten Ausgabenhöhe als zuwendungsfähig anerkannt werden können. Mögliche
				Mehrausgaben gehen zu Lasten des Zuwendungsempfängers. Dieser Mittelabruf wurde automatisch durch das digitale Antragsportal der
				Innovationsförderung Hessen – basierend auf den von Ihnen gemachten Angaben – erstellt und steht unter dem Vorbehalt einer fachlichen
				Prüfung durch die Hessen Agentur.
			</Typography>
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				<strong>Grundsätzlich gilt:</strong> Der Mittelabruf kann maximal in Höhe der bewilligten Förderung erfolgen.
			</Typography>
		</>
	);
};
